import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import { ChevronRight, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { Skeleton } from '@verclam/ui-kit';

const stackStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const skeletonPanelStyle = (padding: string, gap: string) => css`
	display: flex;
	flex-direction: column;
	gap: ${gap};
	padding: ${padding};
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const panelStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	align-items: flex-start;
	padding: 1.25rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const errorPanelStyle = css`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 0.875rem;
	padding: 1.25rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const errorTextStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`;

const errorTitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.4;
	color: ${theme.states.danger.solid};
`;

const errorHintStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.5;
	color: ${theme.colors.inkMuted};
`;

const retryStyle = css`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5625rem 1rem;
	border: 1.5px solid ${theme.colors.borderStrong};
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.surface};
	font-family: ${theme.fonts.body};
	font-size: 0.8125rem;
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
	cursor: pointer;
	transition: border-color ${theme.motion.duration};

	&:hover {
		border-color: ${theme.colors.accent};
	}

	&:focus-visible {
		outline: 3px solid ${theme.colors.accent};
		outline-offset: 2px;
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const emptyTitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.4;
	color: ${theme.colors.ink};
`;

const rangeStyle = css`
	margin: 0;
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	line-height: 1.4;
	color: ${theme.colors.inkMuted};
`;

const actionStyle = css`
	display: inline-flex;
	align-items: center;
	gap: 0.375rem;
	margin-top: 0.25rem;
	padding: 0;
	border: none;
	background: transparent;
	font-family: ${theme.fonts.body};
	font-size: 0.8125rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.3;
	color: ${theme.colors.accent};
	cursor: pointer;

	&:hover {
		color: ${theme.colors.accentDark};
	}

	&:focus-visible {
		outline: 3px solid ${theme.colors.accent};
		outline-offset: 2px;
	}
`;

/** Which block the skeleton stands for: each one reserves the size of its own content. */
export type IndicatorSkeletonVariant = 'documents' | 'beneficiaries' | 'costs';

const DocumentsSkeleton: React.FC = () => (
	<React.Fragment>
		<Skeleton width="11.25rem" height="4.375rem" borderRadius={theme.radii.small} />
		<Skeleton width="min(23.75rem, 100%)" height="0.875rem" borderRadius="7px" />
		<div css={skeletonPanelStyle('1rem', '0.875rem')}>
			<Skeleton height="1.125rem" borderRadius="9px" />
			<Skeleton width="80%" height="1.125rem" borderRadius="9px" />
			<Skeleton width="60%" height="1.125rem" borderRadius="9px" />
			<Skeleton width="40%" height="1.125rem" borderRadius="9px" />
		</div>
	</React.Fragment>
);

const BeneficiariesSkeleton: React.FC = () => (
	<React.Fragment>
		<Skeleton width="7.5rem" height="3rem" borderRadius="12px" />
		<Skeleton width="min(16.25rem, 100%)" height="0.875rem" borderRadius="7px" />
	</React.Fragment>
);

const CostsSkeleton: React.FC = () => (
	<React.Fragment>
		<Skeleton width="8.75rem" height="3rem" borderRadius="12px" />
		<Skeleton width="min(13.75rem, 100%)" height="0.875rem" borderRadius="7px" />
		<div css={skeletonPanelStyle('0.875rem', '0.625rem')}>
			<Skeleton height="0.875rem" borderRadius="7px" />
			<Skeleton width="75%" height="0.875rem" borderRadius="7px" />
		</div>
	</React.Fragment>
);

const SKELETONS: Readonly<Record<IndicatorSkeletonVariant, React.FC>> = {
	documents: DocumentsSkeleton,
	beneficiaries: BeneficiariesSkeleton,
	costs: CostsSkeleton,
};

type Props =
	| { readonly kind: 'loading'; readonly variant: IndicatorSkeletonVariant }
	| { readonly kind: 'error'; readonly onRetry: () => void }
	| {
			readonly kind: 'empty';
			readonly title: string;
			/** Dates of the window, already formatted: tells what "no data" was measured over. */
			readonly range?: string;
			readonly actionLabel?: string;
			readonly onAction?: () => void;
	  };

/** Skeleton, scoped error or empty answer of one indicator block, so a block never blanks its neighbours. */
const IndicatorBlockState: React.FC<Props> = (props) => {
	if (props.kind === 'loading') {
		const Skeletons = SKELETONS[props.variant];

		return (
			<div css={stackStyle} aria-busy="true" role="status" aria-label="Chargement de l'indicateur">
				<Skeletons />
			</div>
		);
	}

	if (props.kind === 'error') {
		return (
			<div css={errorPanelStyle} role="alert">
				<div css={errorTextStyle}>
					<p css={errorTitleStyle}>Indicateur indisponible pour le moment</p>
					<p css={errorHintStyle}>Les autres indicateurs de la page restent à jour.</p>
				</div>
				<button type="button" css={retryStyle} onClick={props.onRetry}>
					<RefreshCw size={14} aria-hidden="true" />
					Réessayer
				</button>
			</div>
		);
	}

	return (
		<div css={panelStyle}>
			<p css={emptyTitleStyle}>{props.title}</p>
			{props.range !== undefined && <p css={rangeStyle}>{props.range}</p>}
			{props.actionLabel !== undefined && props.onAction !== undefined && (
				<button type="button" css={actionStyle} onClick={props.onAction}>
					{props.actionLabel}
					<ChevronRight size={14} aria-hidden="true" />
				</button>
			)}
		</div>
	);
};

export default IndicatorBlockState;
